import { zodResolver } from '@hookform/resolvers/zod';
import { okSchema, passwordSchema } from '@hearth/shared';
import { useMutation } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { FormField } from '@/components/form-field';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { apiRequest } from '@/lib/api';
import { applyServerErrors } from '@/lib/forms';

export const Route = createFileRoute('/_guest/reset-password')({
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    token: typeof search.token === 'string' ? search.token : undefined,
  }),
  component: ResetPasswordPage,
});

const formSchema = z
  .object({ password: passwordSchema, confirm: z.string() })
  .refine((v) => v.password === v.confirm, {
    path: ['confirm'],
    message: "Passwords don't match.",
  });
type Values = z.input<typeof formSchema>;

function ResetPasswordPage() {
  const { token = '' } = Route.useSearch();
  const form = useForm<Values>({ resolver: zodResolver(formSchema) });
  const reset = useMutation({
    mutationFn: (v: Values) =>
      apiRequest('/api/auth/password/reset', {
        method: 'POST',
        body: { token, password: v.password },
        schema: okSchema,
      }),
    onError: (err) => applyServerErrors(err, form.setError),
  });

  if (reset.isSuccess) {
    return (
      <Card>
        <CardHeader>
          <CheckCircle2 className="text-success mb-2 size-8" />
          <CardTitle>Password updated</CardTitle>
          <CardDescription>
            You can now sign in with your new password. Other sessions were signed out.
          </CardDescription>
        </CardHeader>
        <CardFooter>
          <Button asChild className="w-full">
            <Link to="/login" search={{ redirect: undefined }}>
              Sign in
            </Link>
          </Button>
        </CardFooter>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Choose a new password</CardTitle>
        <CardDescription>
          Use at least 10 characters. A passphrase of a few words works well.
        </CardDescription>
      </CardHeader>
      {!token ? (
        <CardContent className="text-destructive text-sm">
          This link is missing its token. Request a new reset email.
        </CardContent>
      ) : (
        <form onSubmit={form.handleSubmit((v) => reset.mutate(v))} noValidate>
          <CardContent className="grid gap-4">
            <FormField label="New password" error={form.formState.errors.password?.message}>
              <Input
                type="password"
                autoComplete="new-password"
                autoFocus
                {...form.register('password')}
              />
            </FormField>
            <FormField label="Confirm password" error={form.formState.errors.confirm?.message}>
              <Input type="password" autoComplete="new-password" {...form.register('confirm')} />
            </FormField>
          </CardContent>
          <CardFooter className="mt-6 flex-col gap-3">
            <Button type="submit" className="w-full" disabled={reset.isPending}>
              {reset.isPending && <Loader2 className="animate-spin" />} Update password
            </Button>
            <Link
              to="/forgot-password"
              className="text-muted-foreground hover:text-foreground text-sm"
            >
              Request a new link
            </Link>
          </CardFooter>
        </form>
      )}
    </Card>
  );
}
